import React, { useState } from 'react';
import { createStyles, makeStyles, Theme, Typography, Box } from '@material-ui/core';
import { Membership, MembershipItem } from '@spike/membership-model';
import { MembershipItemsTable } from 'components/Memberships/Add/Steps/Items/Table/MembershipItemsTable';
import { MembershipDiscountedItemsTable } from 'components/Memberships/Add/Steps/Items/Table/MembershipDiscountedItemsTable';
import { PriceField } from 'components/UI/V3/Forms/PriceField/PriceField';
import Button from 'components/UI/V2/Button/Button';
import useNonInitialEffect from '@versiondos/hooks';

interface MembershipItemsStepProps {
    membership: Membership;
    onNext: (items: MembershipItem[], total: string) => void;
    marketplaceID: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        stepDescription: {
            color: '#7A7A7A',
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 400,
            marginTop: -28,
            marginBottom: 10,

            [theme.breakpoints.up('md')]: {
                marginTop: -20
            }
        },
        submitButton: {
            marginTop: 8,

            [theme.breakpoints.up('md')]: {
                alignSelf: 'flex-start'
            }
        },
        membershipTotalContainer: {
            borderTop: '1px solid #D4D4D4',
            paddingTop: 24,
            marginTop: 24,

            [theme.breakpoints.down('sm')]: {
                marginTop: 32
            }
        },
        membershipTotal: {
            'fontSize': 15,
            'fontWeight': 400,
            'textAlign': 'right',

            '& b': {
                fontSize: 16,
                fontWeight: 600
            }
        },
        membershipTotalFieldContainer: {
            width: 150
        },
        membershipTotalInput: {
            width: 140
        }
    })
);

export const MembershipItemsStep: React.FC<MembershipItemsStepProps> = props => {
    const classes = useStyles();

    // Separate items into regular and discounted on initialization
    const [regularItems, setRegularItems] = React.useState<MembershipItem[]>(() => {
        return props.membership.membershipItems.filter(item => !item.discountAmount && !item.discountPercentage);
    });

    const [discountedItems, setDiscountedItems] = React.useState<MembershipItem[]>(() => {
        return props.membership.membershipItems.filter(item => item.discountAmount || item.discountPercentage);
    });

    const [loading, setLoading] = useState(false);

    // Get excluded item IDs for mutual exclusion
    const regularItemIDs = React.useMemo(() => {
        return regularItems.map(item => item.itemID).filter(Boolean);
    }, [regularItems]);

    const discountedItemIDs = React.useMemo(() => {
        return discountedItems.map(item => item.itemID).filter(Boolean);
    }, [discountedItems]);

    // Check if there are any discounted items
    const hasDiscountedItems = discountedItems.length > 0;

    // Calculate membership total based on items (auto-calculated)
    const calculatedTotal = React.useMemo(() => {
        // Regular items: consider quantity unless unlimited
        const regularTotal = regularItems.reduce((sum, item) => {
            const amount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;
            if (item.unlimited) {
                return sum + amount;
            }
            return sum + amount * item.quantity;
        }, 0);

        // Discounted items: always unlimited, just sum amounts
        const discountedTotal = discountedItems.reduce((sum, item) => {
            const amount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;
            return sum + amount;
        }, 0);

        return regularTotal + discountedTotal;
    }, [regularItems, discountedItems]);

    // Editable total state (allows manual editing)
    // In edit mode, initialize from membership.total if available; otherwise fall back to calculatedTotal
    const [editableTotal, setEditableTotal] = React.useState<number | undefined>(
        props.membership.total !== undefined ? parseFloat(props.membership.total) : calculatedTotal
    );

    // Sync editableTotal with calculatedTotal only when items actually change (not on initial mount)
    useNonInitialEffect(() => {
        setEditableTotal(calculatedTotal);
    }, [calculatedTotal]);

    // Validate: at least one item total, and no empty items
    const isValid = React.useMemo(() => {
        const totalItems = regularItems.length + discountedItems.length;
        const emptyRegular = regularItems.filter(item => !item.itemID).length;
        const emptyDiscounted = discountedItems.filter(item => !item.itemID).length;

        // Validate that all discounted items have a discount > 0
        const discountedItemsWithoutDiscount = discountedItems.filter(
            item => item.itemID && (item.discountAmount || 0) === 0 && (item.discountPercentage || 0) === 0
        ).length;

        // Validate total is a valid number >= 0
        const validTotal = editableTotal !== undefined && editableTotal >= 0;

        return (
            totalItems > 0 &&
            emptyRegular === 0 &&
            emptyDiscounted === 0 &&
            discountedItemsWithoutDiscount === 0 &&
            validTotal
        );
    }, [regularItems, discountedItems, editableTotal]);

    const handleSave = async () => {
        if (loading) return;
        setLoading(true);
        try {
            // Combine both arrays for backend
            const allItems = [...regularItems, ...discountedItems];
            // Convert total to string with 2 decimals for backend
            const totalString = (editableTotal || 0).toFixed(2);
            await props.onNext(allItems, totalString);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Typography className={classes.stepDescription}>
                Add the services, add-ons, and products included in the membership.
            </Typography>

            <MembershipItemsTable
                items={regularItems}
                onChange={setRegularItems}
                excludedItemIDs={discountedItemIDs}
                marketplaceID={props.marketplaceID}
            />

            <MembershipDiscountedItemsTable
                items={discountedItems}
                onChange={setDiscountedItems}
                excludedItemIDs={regularItemIDs}
                marketplaceID={props.marketplaceID}
            />

            <div className={classes.membershipTotalContainer}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    style={{ gap: 16 }}
                >
                    <Typography
                        variant="body1"
                        style={{ fontWeight: 600, fontSize: 16 }}
                    >
                        Membership Total
                    </Typography>
                    <div className={classes.membershipTotalFieldContainer}>
                        <PriceField
                            value={editableTotal}
                            onChange={value => setEditableTotal(value)}
                            className={classes.membershipTotalInput}
                            scale={2}
                            precision={6}
                            autoFocus={false}
                        />
                    </div>
                </Box>
            </div>

            <Button
                label="Save"
                size="large"
                disabled={!isValid || loading}
                className={classes.submitButton}
                onClick={handleSave}
            />
        </>
    );
};
